/**
 * Demand fulfilment output view — ports DemandFulfil.jsx from the design handoff.
 * PanelHead with summary metrics, Met/Short status strip, per-SKU table
 * with magnitude bar (width ∝ demand) + fill portion + shortfall hatch.
 * Sorted ascending by fill% (worst-first).
 */
import { memo, useMemo } from 'react'
import { Box, Tooltip } from '@mui/material'
import InventoryIcon from '@mui/icons-material/Inventory2'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import { TOKENS, TYPE, FONT_MONO, SchChip } from './SchedulerDesign'
import { InputRow } from 'Features/SchedulerView/SchedulerView.types'
import { ProductionRequirementRowType } from './Scheduler.types'

// ─── PanelHead ────────────────────────────────────────────────────────────────

type PanelMetric = {
  label: string
  value: number | string
  unit?: string
  tone?: string
}

const PanelHead = memo(
  ({
    icon,
    title,
    sub,
    metrics,
  }: {
    icon: React.ReactNode
    title: string
    sub?: string
    metrics?: PanelMetric[]
  }) => (
    <Box
      sx={{
        flexShrink: 0,
        borderBottom: `1px solid ${TOKENS.line200}`,
        px: '18px',
        py: '11px',
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
      }}
    >
      <Box
        sx={{
          color: TOKENS.stateComplete,
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.18 }}>
        <Box sx={{ ...TYPE.h2 }}>{title}</Box>
        {sub && <Box sx={{ fontSize: 10.5, color: TOKENS.ink400 }}>{sub}</Box>}
      </Box>
      <Box sx={{ flex: 1 }} />
      {metrics && (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {metrics.map((m, i) => (
            <Box
              key={m.label}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1px',
                pr: i < metrics.length - 1 ? '18px' : 0,
                borderRight:
                  i < metrics.length - 1
                    ? `1px solid ${TOKENS.line200}`
                    : 'none',
                mr: i < metrics.length - 1 ? '18px' : 0,
              }}
            >
              <Box
                sx={{
                  fontSize: 9,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.07em',
                  color: TOKENS.ink400,
                }}
              >
                {m.label}
              </Box>
              <Box
                sx={{
                  fontFamily: FONT_MONO,
                  fontSize: 17,
                  fontWeight: 600,
                  lineHeight: 1,
                  color: m.tone ?? TOKENS.ink900,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {m.value}
                {m.unit && (
                  <Box
                    component="span"
                    sx={{ fontSize: 10, color: TOKENS.ink400, ml: '2px' }}
                  >
                    {m.unit}
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
)
PanelHead.displayName = 'PanelHead'

// ─── Data types & derivation ──────────────────────────────────────────────────

const dfK = (n: number) =>
  n >= 1000
    ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`
    : String(Math.round(n))

type DemandItem = {
  code: string
  name: string
  demand: number
  produced: number
  pct: number
  status: 'met' | 'short'
}

function deriveDemandData(
  productionRequirements: ProductionRequirementRowType[],
  outputRows: InputRow[]
) {
  const scheduledMap = new Map<string, number>()
  for (const row of outputRows) {
    if (!row.productCode) continue
    const qty =
      typeof row.quantity === 'string'
        ? parseFloat(row.quantity) || 0
        : (row.quantity ?? 0)
    const key = String(row.productCode)
    scheduledMap.set(key, (scheduledMap.get(key) ?? 0) + qty)
  }

  const rows: DemandItem[] = productionRequirements
    .map((r) => {
      const baseQty = parseFloat(String(r.quantity ?? 0)) || 0
      if (baseQty <= 0) return null
      const stmRaw = (r as Record<string, unknown>).quantityStillToMake
      const stm = stmRaw != null ? Number(stmRaw) : null
      let demand: number
      if (stm !== null) {
        if (stm <= 0) return null // stm=0 → already produced, not unmet
        demand = stm
      } else {
        const made =
          Number((r as Record<string, unknown>).quantityMade ?? 0) || 0
        demand = Math.max(0, baseQty - made)
        if (demand <= 0) return null
      }
      const produced = Math.min(
        demand,
        scheduledMap.get(String(r.productCode)) ?? 0
      )
      const pct = demand > 0 ? produced / demand : 1
      return {
        code: r.productCode,
        name: r.productName ?? r.productCode,
        demand,
        produced,
        pct,
        status: produced >= demand * 0.99 ? 'met' : 'short',
      } as DemandItem
    })
    .filter((r): r is DemandItem => r !== null)
    .sort((a, b) => a.pct - b.pct)

  const totalDemand = rows.reduce((s, r) => s + r.demand, 0)
  const totalProduced = rows.reduce((s, r) => s + r.produced, 0)
  return {
    rows,
    summary: {
      demand: totalDemand,
      produced: totalProduced,
      pct:
        totalDemand > 0 ? Math.round((totalProduced / totalDemand) * 100) : 100,
      unmet: Math.max(0, totalDemand - totalProduced),
      met: rows.filter((r) => r.status === 'met').length,
      short: rows.filter((r) => r.status === 'short').length,
    },
  }
}

// ─── DemandRow ────────────────────────────────────────────────────────────────

const DemandRow = memo(
  ({ row, maxDemand }: { row: DemandItem; maxDemand: number }) => {
    const isShort = row.status === 'short'
    const tone = isShort ? TOKENS.ganttDowntime : TOKENS.stateComplete
    const trackW = maxDemand > 0 ? (row.demand / maxDemand) * 100 : 0
    const unmet = row.demand - row.produced

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          height: 34,
          borderBottom: `1px solid ${TOKENS.line100}`,
        }}
      >
        {/* Name + code */}
        <Box
          sx={{
            width: 168,
            minWidth: 168,
            display: 'flex',
            flexDirection: 'column',
            lineHeight: 1.2,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: TOKENS.ink900,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {row.name}
          </Box>
          <Box
            sx={{ fontSize: 9.5, color: TOKENS.ink300, fontFamily: FONT_MONO }}
          >
            {row.code}
          </Box>
        </Box>
        {/* Magnitude bar + fill */}
        <Box
          sx={{ flex: 1, minWidth: 90, display: 'flex', alignItems: 'center' }}
        >
          <Tooltip
            title={`${dfK(row.produced)} of ${dfK(row.demand)} produced`}
            arrow
          >
            <Box
              sx={{
                width: `${trackW}%`,
                minWidth: 4,
                height: 14,
                display: 'flex',
                borderRadius: 3,
                overflow: 'hidden',
                border: `1px solid ${TOKENS.line200}`,
              }}
            >
              <Box
                sx={{
                  width: `${row.pct * 100}%`,
                  bgcolor: TOKENS.ganttProduction,
                }}
              />
              {unmet > 0 && (
                <Box
                  sx={{
                    flex: 1,
                    background:
                      'repeating-linear-gradient(45deg, rgba(196,69,54,0.22) 0 4px, rgba(196,69,54,0.40) 4px 8px)',
                  }}
                />
              )}
            </Box>
          </Tooltip>
        </Box>
        {/* Fill % */}
        <Box
          sx={{
            width: 50,
            textAlign: 'right',
            fontFamily: FONT_MONO,
            fontSize: 13,
            fontWeight: 600,
            color: tone,
          }}
        >
          {Math.round(row.pct * 100)}%
        </Box>
        {/* Produced / demand */}
        <Box
          sx={{
            width: 96,
            textAlign: 'right',
            fontFamily: FONT_MONO,
            fontSize: 11,
            color: TOKENS.ink500,
          }}
        >
          {dfK(row.produced)} / {dfK(row.demand)}
        </Box>
        {/* Unmet */}
        <Box
          sx={{
            width: 78,
            textAlign: 'right',
            fontFamily: FONT_MONO,
            fontSize: 11,
            color: unmet > 0 ? TOKENS.ganttDowntime : TOKENS.ink300,
          }}
        >
          {unmet > 0 ? `−${dfK(unmet)}` : '—'}
        </Box>
        {/* Status chip */}
        <Box sx={{ width: 74 }}>
          <SchChip tone={isShort ? 'failed' : 'complete'}>
            {isShort ? 'Short' : 'Met'}
          </SchChip>
        </Box>
      </Box>
    )
  }
)
DemandRow.displayName = 'DemandRow'

// ─── Main export ──────────────────────────────────────────────────────────────

export const ScheduleDemandFulfilmentView = memo(
  ({
    outputRows,
    productionRequirements,
  }: {
    outputRows: InputRow[]
    productionRequirements: ProductionRequirementRowType[]
  }) => {
    const { rows, summary } = useMemo(
      () => deriveDemandData(productionRequirements, outputRows),
      [productionRequirements, outputRows]
    )
    const maxDemand = rows.length ? Math.max(...rows.map((r) => r.demand)) : 1
    const fulfilTone =
      summary.pct >= 98
        ? TOKENS.stateComplete
        : summary.pct >= 92
          ? TOKENS.stateRunning
          : TOKENS.ganttDowntime

    const STATUS_CARDS = [
      {
        key: 'met' as const,
        count: summary.met,
        label: 'Met',
        color: TOKENS.stateComplete,
        icon: <CheckCircleIcon sx={{ fontSize: 20 }} />,
      },
      {
        key: 'short' as const,
        count: summary.short,
        label: 'Short',
        color: TOKENS.ganttDowntime,
        icon: <ErrorIcon sx={{ fontSize: 20 }} />,
      },
    ]

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          bgcolor: TOKENS.surface0,
        }}
      >
        <PanelHead
          icon={<InventoryIcon sx={{ fontSize: 19 }} />}
          title="Demand fulfilment"
          sub="Scheduled production against required demand, by SKU"
          metrics={[
            {
              label: 'Fulfilment',
              value: summary.pct,
              unit: '%',
              tone: fulfilTone,
            },
            { label: 'Demand', value: dfK(summary.demand) },
            { label: 'Produced', value: dfK(summary.produced) },
            {
              label: 'Unmet',
              value: dfK(summary.unmet),
              tone: summary.unmet ? TOKENS.ganttDowntime : TOKENS.stateComplete,
            },
          ]}
        />
        <Box sx={{ overflow: 'auto', px: '18px', py: '16px', pb: '24px' }}>
          {/* Status strip */}
          <Box sx={{ display: 'flex', gap: '10px', mb: '18px' }}>
            {STATUS_CARDS.map(({ key, count, label, color, icon }) => (
              <Box
                key={key}
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  p: '11px 14px',
                  border: `1px solid ${TOKENS.line200}`,
                  borderRadius: '3px',
                  bgcolor: '#fff',
                  borderLeft: `3px solid ${color}`,
                }}
              >
                <Box sx={{ color }}>{icon}</Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    lineHeight: 1.1,
                  }}
                >
                  <Box
                    sx={{
                      fontFamily: FONT_MONO,
                      fontSize: 22,
                      fontWeight: 700,
                      color: TOKENS.ink900,
                    }}
                  >
                    {count}
                  </Box>
                  <Box
                    sx={{
                      fontSize: 10.5,
                      color: TOKENS.ink400,
                      fontWeight: 600,
                    }}
                  >
                    {label} {count === 1 ? 'SKU' : 'SKUs'}
                  </Box>
                </Box>
                <Box sx={{ flex: 1 }} />
                <Box
                  sx={{
                    fontFamily: FONT_MONO,
                    fontSize: 12,
                    color: TOKENS.ink300,
                  }}
                >
                  {rows.length > 0
                    ? `${Math.round((count / rows.length) * 100)}%`
                    : '—'}
                </Box>
              </Box>
            ))}
          </Box>

          {/* Column header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              height: 22,
            }}
          >
            <Box
              sx={{
                width: 168,
                fontSize: 8.5,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: TOKENS.ink300,
              }}
            >
              Product
            </Box>
            <Box
              sx={{
                flex: 1,
                fontSize: 8.5,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: TOKENS.ink300,
              }}
            >
              Produced vs demand
            </Box>
            <Box
              sx={{
                width: 50,
                textAlign: 'right',
                fontSize: 8.5,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: TOKENS.ink300,
              }}
            >
              Fill
            </Box>
            <Box
              sx={{
                width: 96,
                textAlign: 'right',
                fontSize: 8.5,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: TOKENS.ink300,
              }}
            >
              Units
            </Box>
            <Box
              sx={{
                width: 78,
                textAlign: 'right',
                fontSize: 8.5,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: TOKENS.ink300,
              }}
            >
              Unmet
            </Box>
            <Box sx={{ width: 74 }} />
          </Box>

          {/* Rows */}
          {rows.map((r) => (
            <DemandRow key={r.code} row={r} maxDemand={maxDemand} />
          ))}

          {rows.length === 0 && (
            <Box
              sx={{
                p: 4,
                textAlign: 'center',
                color: TOKENS.ink400,
                fontSize: 12.5,
              }}
            >
              No production requirements data.
            </Box>
          )}
        </Box>
      </Box>
    )
  }
)
ScheduleDemandFulfilmentView.displayName = 'ScheduleDemandFulfilmentView'
